import { z } from "zod";

import { type FormValidationOutput } from "./base";

const emailAddressSchema = z.string().email();

const commonEmailDomains = [
  // Global providers
  "gmail.com",
  "yahoo.com",
  "hotmail.com",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "protonmail.com",
  "mail.com",
  "yandex.com",
  "zoho.com",

  // China
  "qq.com",
  "163.com",
  "126.com",
  "sina.com",
  "sohu.com",
  "139.com",
  "wo.cn",
  "189.cn",
  "yeah.net",
  "foxmail.com",
  "aliyun.com",
  "tom.com",
  "vip.sina.com",
  "vip.163.com",
  "vip.126.com",

  // Japan
  "yahoo.co.jp",
  "docomo.ne.jp",
  "softbank.ne.jp",
  "au.com",
  "ezweb.ne.jp",
  "nifty.com",
  "biglobe.ne.jp",
  "so-net.ne.jp",
  "ocn.ne.jp",
  "rakuten.jp",

  // South Korea
  "naver.com",
  "hanmail.net",
  "daum.net",
  "nate.com",
  "korea.com",
  "chol.com",
  "empal.com",
  "dreamwiz.com",

  // India
  "rediffmail.com",
  "sify.com",
  "indiatimes.com",
  "yahoo.in",
  "in.com",
  "mailcity.com",
  "sancharnet.in",
  "vsnl.com",

  // Southeast Asia
  "yahoo.com.sg",
  "singnet.com.sg",
  "pacific.net.sg",
  "yahoo.com.my",
  "tm.net.my",
  "streamyx.com",
  "yahoo.co.th",
  "hotmail.co.th",
  "yahoo.com.ph",
  "yahoo.co.id",
  "telkom.net",
  "indosat.net.id",
  "yahoo.com.vn",
  "gmail.vn",
  "fpt.vn",

  // Middle East
  "maktoob.com",
  "yahoo.com.sa",
  "hotmail.sa",
  "gmail.sa",
  "yahoo.ae",
  "eim.ae",
  "yahoo.com.eg",
  "link.net",

  // Europe
  "web.de",
  "gmx.de",
  "t-online.de",
  "freenet.de",
  "yahoo.de",
  "mail.ru",
  "yandex.ru",
  "rambler.ru",
  "inbox.ru",
  "bk.ru",
  "list.ru",
  "yahoo.fr",
  "laposte.net",
  "orange.fr",
  "wanadoo.fr",
  "free.fr",
  "yahoo.co.uk",
  "btinternet.com",
  "virgin.net",
  "tiscali.co.uk",
  "yahoo.it",
  "libero.it",
  "virgilio.it",
  "alice.it",
  "yahoo.es",
  "terra.es",
  "ya.com",
  "yahoo.com.br",
  "uol.com.br",
  "bol.com.br",
  "terra.com.br",
  "ig.com.br",

  // Australia/New Zealand
  "yahoo.com.au",
  "bigpond.com",
  "optusnet.com.au",
  "telstra.com",
  "yahoo.co.nz",
  "xtra.co.nz",

  // Africa
  "yahoo.co.za",
  "webmail.co.za",
  "mweb.co.za",
  "telkomsa.net",
  "yahoo.com.ng",
  "yahoo.co.ke",

  // Latin America
  "yahoo.com.mx",
  "prodigy.net.mx",
  "yahoo.com.ar",
  "fibertel.com.ar",
  "yahoo.com.co",
  "yahoo.com.ve",
  "cantv.net",
  "yahoo.cl",
  "yahoo.com.pe",
];

const commonTypos = [
  // Global providers
  { typo: "gmail.con", correct: "gmail.com" },
  { typo: "gmail.co", correct: "gmail.com" },
  { typo: "gmai.com", correct: "gmail.com" },
  { typo: "gmial.com", correct: "gmail.com" },
  { typo: "gmail.om", correct: "gmail.com" },
  { typo: "yahoo.con", correct: "yahoo.com" },
  { typo: "yahoo.co", correct: "yahoo.com" },
  { typo: "yaho.com", correct: "yahoo.com" },
  { typo: "yahoo.om", correct: "yahoo.com" },
  { typo: "hotmail.con", correct: "hotmail.com" },
  { typo: "hotmail.co", correct: "hotmail.com" },
  { typo: "hotmai.com", correct: "hotmail.com" },
  { typo: "hotmail.om", correct: "hotmail.com" },
  { typo: "outlook.con", correct: "outlook.com" },
  { typo: "outlook.co", correct: "outlook.com" },
  { typo: "outlok.com", correct: "outlook.com" },
  { typo: "outlook.om", correct: "outlook.com" },
  { typo: "icloud.con", correct: "icloud.com" },
  { typo: "icloud.co", correct: "icloud.com" },
  { typo: "iclod.com", correct: "icloud.com" },
  { typo: "icloud.om", correct: "icloud.com" },

  // Chinese providers
  { typo: "qq.con", correct: "qq.com" },
  { typo: "qq.co", correct: "qq.com" },
  { typo: "163.con", correct: "163.com" },
  { typo: "163.co", correct: "163.com" },
  { typo: "126.con", correct: "126.com" },
  { typo: "126.co", correct: "126.com" },
  { typo: "sina.con", correct: "sina.com" },
  { typo: "sina.co", correct: "sina.com" },
  { typo: "sohu.con", correct: "sohu.com" },
  { typo: "sohu.co", correct: "sohu.com" },
  { typo: "foxmail.con", correct: "foxmail.com" },
  { typo: "foxmail.co", correct: "foxmail.com" },

  // Japanese providers
  { typo: "yahoo.jp", correct: "yahoo.co.jp" },
  { typo: "docomo.jp", correct: "docomo.ne.jp" },
  { typo: "docomo.com", correct: "docomo.ne.jp" },
  { typo: "softbank.jp", correct: "softbank.ne.jp" },
  { typo: "softbank.com", correct: "softbank.ne.jp" },

  // Korean providers
  { typo: "naver.con", correct: "naver.com" },
  { typo: "naver.co", correct: "naver.com" },
  { typo: "daum.con", correct: "daum.net" },
  { typo: "daum.co", correct: "daum.net" },
  { typo: "hanmail.con", correct: "hanmail.net" },
  { typo: "hanmail.co", correct: "hanmail.net" },

  // European providers
  { typo: "web.con", correct: "web.de" },
  { typo: "web.co", correct: "web.de" },
  { typo: "gmx.con", correct: "gmx.de" },
  { typo: "gmx.co", correct: "gmx.de" },
  { typo: "mail.con", correct: "mail.ru" },
  { typo: "mail.co", correct: "mail.ru" },
  { typo: "yandex.con", correct: "yandex.ru" },
  { typo: "yandex.co", correct: "yandex.ru" },

  // Indian providers
  { typo: "rediffmail.con", correct: "rediffmail.com" },
  { typo: "rediffmail.co", correct: "rediffmail.com" },
  { typo: "yahoo.con", correct: "yahoo.in" },
  { typo: "yahoo.co", correct: "yahoo.in" },
];

export function detectEmailTypo(email: string): string | null {
  const [localPart, domain] = email.split("@");
  if (!domain) return null;

  const domainLower = domain.toLowerCase();

  // Check if the domain is already correct
  if (commonEmailDomains.includes(domainLower)) {
    return null;
  }

  // Check common typos first
  for (const { typo, correct } of commonTypos) {
    if (domainLower === typo) {
      return `${localPart}@${correct}`;
    }
  }

  // Check for similar domains using edit distance
  for (const correctDomain of commonEmailDomains) {
    if (isTypoOf(domainLower, correctDomain)) {
      return `${localPart}@${correctDomain}`;
    }
  }

  return null;
}

function isTypoOf(typed: string, correct: string): boolean {
  if (Math.abs(typed.length - correct.length) > 2) return false;

  // Calculate Levenshtein distance
  const matrix: number[][] = Array.from({ length: typed.length + 1 }, () =>
    Array.from({ length: correct.length + 1 }, () => 0),
  );

  for (let i = 0; i <= typed.length; i++) {
    const row = matrix[i];
    if (row) row[0] = i;
  }

  for (let j = 0; j <= correct.length; j++) {
    const firstRow = matrix[0];
    if (firstRow) firstRow[j] = j;
  }

  for (let i = 1; i <= typed.length; i++) {
    for (let j = 1; j <= correct.length; j++) {
      const currentRow = matrix[i];
      const prevRow = matrix[i - 1];

      if (currentRow && prevRow) {
        if (typed[i - 1] === correct[j - 1]) {
          const prevValue = prevRow[j - 1];
          if (prevValue !== undefined) currentRow[j] = prevValue;
        } else {
          const deletion = prevRow[j];
          const insertion = currentRow[j - 1];
          const substitution = prevRow[j - 1];

          if (
            deletion !== undefined &&
            insertion !== undefined &&
            substitution !== undefined
          ) {
            currentRow[j] = Math.min(
              deletion + 1, // deletion
              insertion + 1, // insertion
              substitution + 1, // substitution
            );
          }
        }
      }
    }
  }

  const lastRow = matrix[typed.length];
  const distance = lastRow ? lastRow[correct.length] : undefined;
  if (distance === undefined) return false;
  const maxDistance = Math.max(1, Math.floor(correct.length / 4));

  return distance > 0 && distance <= maxDistance && distance <= 2;
}

export function isValidEmailAddress(email: string): FormValidationOutput {
  try {
    emailAddressSchema.parse(email);
    return { isValid: true, message: "" };
  } catch (e) {
    if (e instanceof z.ZodError) {
      return {
        isValid: false,
        message: e.issues[0]?.message ?? "Invalid email address.",
      };
    }
    return { isValid: false, message: "Invalid email address." };
  }
}

export function getEmailTypoSuggestion(email: string): string | null {
  // Only suggest typos for valid-format emails
  const validationResult = isValidEmailAddress(email);
  if (!validationResult.isValid) {
    return null;
  }

  return detectEmailTypo(email);
}
